import React from 'react';

import type Event from '@bugsnag/core/types/event';
import Bugsnag, { Config, NotifiableError } from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';

import version from '../../package.json';

export type BugsnagConfigType = Config & { beforeSend?: (event: Event) => boolean | Event };

export const isValidationError = (event: Event): boolean => {
    const errors = Array.from(event?.errors ?? []);
    return errors.some((err) => {
        return err.errorClass === 'ValidationError';
    });
};

export const isResizeObserverError = (event: Event): boolean => {
    const errors = Array.from(event?.errors ?? []);

    return errors.some((err) => {
        if (err?.errorClass?.includes('ResizeObserver')) {
            return true;
        }

        const message = String(err?.errorMessage ?? '').trim();

        return message.includes('ResizeObserver'.trim());
    });
};

export const bugsnagConfig: BugsnagConfigType = {
    apiKey: `${process.env.FRONTEND_BUGSNAG_API_KEY}`,
    releaseStage: `${process.env.STAGE}`,
    plugins: [new BugsnagPluginReact()],
    appVersion: version.version.toString(),
    enabledReleaseStages: ['prod', 'uat', 'dev'],
    onError: (event: Event) => {
        const currentRoute = window.location.pathname;
        const formattedRoute = currentRoute.replace(/\/(\d+)/g, '/:$1');
        event.context = formattedRoute;

        if (typeof event.addMetadata === 'function') {
            event.addMetadata('route', {
                actualPath: currentRoute,
                templatedPath: formattedRoute,
            });
        }

        if (isValidationError(event)) {
            return false;
        }

        if (isResizeObserverError(event)) {
            return false;
        }
    },
    beforeSend: (event: Event) => {
        if (isValidationError(event)) {
            return false;
        }

        if (isResizeObserverError(event)) {
            return false;
        }

        return event;
    },
};

export const initializeBugsnagClient = () => {
    return Bugsnag.createClient(bugsnagConfig);
};

export const bugsnagClient = initializeBugsnagClient();

export const LogError = (error: NotifiableError) => {
    const userJson = localStorage.getItem('user');
    const user = userJson ? JSON.parse(userJson) : null;

    if (user) {
        const { id, email, username } = user;

        if (process.env.STAGE === 'local') {
            console.error({ error, userId: id });
        } else {
            console.error({ error, userId: id });
            bugsnagClient.setUser(id.toString(), email, username);
            bugsnagClient.notify(error);
        }
    } else {
        console.error({ error, userId: undefined });
        bugsnagClient.notify(error);
    }
};

export const ErrorBoundary = bugsnagClient.getPlugin('react').createErrorBoundary(React);
